// OutOfRegionBanner
// ==================
//
// Wave 14dz: Wenn ein User die App testet, aber GPS-mäßig weit weg von
// Liguria/Elba/Finale Outdoor sitzt (z. B. Freund-Test aus Deutschland),
// dann wirkt die App "kaputt" — keine Trails in der Nähe, Routing schlägt
// fehl, Battery-Stationen sind nicht da. Diese Komponente erkennt das
// und blendet einen freundlichen Hinweis-Banner ein, damit der User
// versteht WARUM nichts geht.
//
// Logik:
//   - subscribe auf positionBus → kennen die aktuelle GPS-Position.
//   - Wenn Position vorhanden UND außerhalb ALLER REGIONS.bounds →
//     Banner anzeigen.
//   - Dismissable per X. Wir merken uns das in sessionStorage damit der
//     Hinweis pro Session nur einmal kommt — bei jedem App-Reload kommt
//     er aber wieder, falls der User immer noch außerhalb ist.
//   - Komponente self-contained, rendert sich selber als absoluter Top-
//     Banner (z-30 — unter Modals aber über der Karte und unter TabBar).

import { useEffect, useState } from 'react';
import { positionBus } from '../lib/positionBus';
import { hasAnyRegion, isInsideAnyRegion, regionNamesList } from '../lib/regionCheck';

const DISMISS_KEY = 'adt_out_of_region_dismissed_session';

export function OutOfRegionBanner() {
  const [position, setPosition] = useState<GeolocationCoordinates | null>(
    () => positionBus.get(),
  );
  const [dismissedThisSession, setDismissedThisSession] = useState<boolean>(
    () => {
      try {
        return window.sessionStorage.getItem(DISMISS_KEY) === '1';
      } catch {
        return false;
      }
    },
  );

  useEffect(() => positionBus.subscribe(setPosition), []);

  // Kein Position-Fix → Banner ausblenden. Wir zeigen ihn NICHT als
  // "keine GPS-Position"-Warnung, sondern wirklich nur wenn wir wissen
  // dass der User real außerhalb ist. Sonst wäre der Hinweis bei jedem
  // App-Start zu sehen, bevor irgendwer den Locate-Button gedrückt hat.
  if (!position) return null;
  if (dismissedThisSession) return null;
  // Wave 14ed: Noch gar keine Region (frischer Account, kein Korridor
  // gebaut)? Dann ist der User nicht "ausserhalb" von irgendwas — der
  // Banner waere schlicht falsch und wuerde jeden neuen User begruessen.
  if (!hasAnyRegion()) return null;
  if (isInsideAnyRegion(position.latitude, position.longitude)) return null;

  const dismiss = () => {
    try {
      window.sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* Private-Mode → halt nochmal nervig, akzeptabel */
    }
    setDismissedThisSession(true);
  };

  return (
    <div
      className="absolute left-3 right-3 z-30 pointer-events-auto"
      style={{
        top: 'calc(env(safe-area-inset-top, 0px) + 0.5rem)',
      }}
      role="status"
      aria-live="polite"
    >
      <div
        className="rounded-xl shadow-lg flex items-start gap-3 px-3 py-2.5"
        style={{
          background: 'rgba(180, 83, 9, 0.95)', // amber-700-ish
          color: '#fff',
          border: '1px solid rgba(252, 211, 77, 0.5)',
        }}
      >
        <span
          className="text-lg leading-none flex-shrink-0 mt-0.5"
          aria-hidden="true"
        >
          📍
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">
            You're outside our supported regions
          </div>
          <div className="text-[12px] mt-0.5 leading-snug text-white/90">
            All Day Turbo ULTRA's offline maps, trails and routing only work in{' '}
            {regionNamesList()}. Things will look empty here.
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white/90 hover:bg-white/10 active:bg-white/20 text-lg leading-none"
          aria-label="Dismiss"
        >
          ×
        </button>
      </div>
    </div>
  );
}
